<template>
    <PageHeader :title="t('team.title')" />
    <div class="page-wrapper" style="padding-top: calc(50px * 1.1)!important;">
        <div class="team-container">
            <div class="team-stats">
                <div class="stat-card">
                    <div class="stat-icon">👥</div>
                    <div class="stat-info">
                        <div class="stat-label">{{ t('team.totalMembers') }}</div>
                        <div class="stat-value">{{ displayTotalMembers }}</div>
                    </div>
                </div>
                <div class="stat-card">
                    <div class="stat-icon">💰</div>
                    <div class="stat-info">
                        <div class="stat-label">{{ t('team.totalCommission') }}</div>
                        <div class="stat-value">{{ appConfig.currency.symbol }}{{ displayTotalCommission }}</div>
                    </div>
                </div>
            </div>

            <div class="level-tabs">
                <div class="level-tab" :class="{ 'active': currentLevel === 1 }" @click="switchLevel(1)">
                    <div class="tab-content">
                        <span class="tab-title">{{ t('team.level1') }}</span>
                        <span class="tab-count">{{ level1Count }}</span>
                    </div>
                </div>
                <div class="level-tab" :class="{ 'active': currentLevel === 2 }" @click="switchLevel(2)">
                    <div class="tab-content">
                        <span class="tab-title">{{ t('team.level2') }}</span>
                        <span class="tab-count">{{ level2Count }}</span>
                    </div>
                </div>
                <div class="level-tab" :class="{ 'active': currentLevel === 3 }" @click="switchLevel(3)">
                    <div class="tab-content">
                        <span class="tab-title">{{ t('team.level3') }}</span>
                        <span class="tab-count">{{ level3Count }}</span>
                    </div>
                </div>
            </div>

            <div class="team-list">
                <div class="list-header">
                    <span class="header-title">{{ getLevelTitle() }}</span>
                    <span class="header-count">{{ getCurrentTeamList().length }} {{ t('team.members') }}</span>
                </div>
                
                <div class="team-items">
                    <div class="team-item" v-for="member in displayMembers" :key="member.id">
                        <div class="member-info">
                            <div class="member-avatar">
                                <span class="avatar-text">{{ member.name.charAt(0) }}</span>
                            </div>
                            <div class="member-details">
                                <div class="member-name">{{ maskName(member.name) }}</div>
                                <div class="member-phone">{{ maskPhone(member.phone) }}</div>
                            </div>
                        </div>
                        <div class="member-stats">
                            <div class="stat-row">
                                <span class="stat-label">{{ t('team.orders') }}</span>
                                <span class="stat-value">{{ member.orders }}</span>
                            </div>
                            <div class="stat-row">
                                <span class="stat-label">{{ t('team.commission') }}</span>
                                <span class="stat-value">{{ appConfig.currency.symbol }}{{ member.commission }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="empty-state" v-if="getCurrentTeamList().length === 0">
                        <div class="empty-icon">📭</div>
                        <div class="empty-text">{{ t('team.noMembers') }}</div>
                    </div>

                    <div class="loading-more" v-if="isLoading">
                        <div class="loading-spinner"></div>
                        <span class="loading-text">{{ t('team.loading') }}</span>
                    </div>

                    <div class="no-more" v-if="!isLoading && displayMembers.length >= getCurrentTeamList().length && displayMembers.length > 0">
                        <span class="no-more-text">{{ t('team.noMore') }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <el-backtop :bottom="30" />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTransition } from '@vueuse/core'
import { ElBacktop } from 'element-plus'
import { appConfig } from '@/config/app'
import { getTeamInfo, getTeamList } from '@/api'
import PageHeader from '@/components/PageHeader.vue'

const { t } = useI18n()

const currentLevel = ref(1)
const pageSize = 10
const displayMembers = ref([])
const isLoading = ref(false)

const teamInfo = ref({
  total_members: 0,
  total_commission: 0,
  level1_count: 0,
  level2_count: 0,
  level3_count: 0
})

const level1Members = ref({
  list: [],
  total: 0,
  page: 1
})
const level2Members = ref({
  list: [],
  total: 0,
  page: 1
})
const level3Members = ref({
  list: [],
  total: 0,
  page: 1
})

const level1Count = computed(() => teamInfo.value.level1_count)
const level2Count = computed(() => teamInfo.value.level2_count)
const level3Count = computed(() => teamInfo.value.level3_count)

const totalMembers = ref(0)
const animatedTotalMembers = useTransition(totalMembers, {
  duration: 800,
  initialValue: 0
})

const totalCommission = ref(0)
const animatedTotalCommission = useTransition(totalCommission, {
  duration: 800,
  initialValue: 0
})

const displayTotalMembers = computed(() => {
  const val = animatedTotalMembers.value
  return Number.isFinite(val) ? Math.round(val) : 0
})

const displayTotalCommission = computed(() => {
  const val = animatedTotalCommission.value
  return Number.isFinite(val) ? val.toFixed(2) : '0.00'
})

const fetchTeamInfo = async () => {
  try {
    const response = await getTeamInfo()
    if (response.data.code === 200) {
      const data = response.data.data
      teamInfo.value = {
        total_members: data.total_members,
        total_commission: data.total_commission,
        level1_count: data.level1_count,
        level2_count: data.level2_count,
        level3_count: data.level3_count
      }
      totalMembers.value = data.total_members
      totalCommission.value = parseFloat(data.total_commission)
    }
  } catch (error) {
    console.error('获取团队信息失败:', error)
  }
}

const fetchTeamList = async (level, page = 1, limit = pageSize) => {
  try {
    const response = await getTeamList({ level, page, limit })
    if (response.data.code === 200) {
      const members = response.data.data.list.map(item => ({
        id: item.id,
        name: item.username,
        phone: item.tel,
        orders: item.order_count,
        commission: item.commission
      }))
      
      switch (level) {
        case 1:
          if (page === 1) {
            level1Members.value.list = members
          } else {
            level1Members.value.list = [...level1Members.value.list, ...members]
          }
          level1Members.value.total = response.data.data.total
          level1Members.value.page = page
          break
        case 2:
          if (page === 1) {
            level2Members.value.list = members
          } else {
            level2Members.value.list = [...level2Members.value.list, ...members]
          }
          level2Members.value.total = response.data.data.total
          level2Members.value.page = page
          break
        case 3:
          if (page === 1) {
            level3Members.value.list = members
          } else {
            level3Members.value.list = [...level3Members.value.list, ...members]
          }
          level3Members.value.total = response.data.data.total
          level3Members.value.page = page
          break
      }
    }
  } catch (error) {
    console.error('获取团队列表失败:', error)
  }
}

const getCurrentTeamList = () => {
    switch (currentLevel.value) {
        case 1:
            return level1Members.value.list
        case 2:
            return level2Members.value.list
        case 3:
            return level3Members.value.list
        default:
            return []
    }
}

const getCurrentTotal = () => {
    switch (currentLevel.value) {
        case 1:
            return level1Members.value.total
        case 2:
            return level2Members.value.total
        case 3:
            return level3Members.value.total
        default:
            return 0
    }
}

const getCurrentPage = () => {
    switch (currentLevel.value) {
        case 1:
            return level1Members.value.page
        case 2:
            return level2Members.value.page
        case 3:
            return level3Members.value.page
        default:
            return 1
    }
}

const loadMore = async () => {
    if (isLoading.value) return
    
    const currentTotal = getCurrentTotal()
    if (displayMembers.value.length >= currentTotal) return
    
    isLoading.value = true
    
    const nextPage = getCurrentPage() + 1
    await fetchTeamList(currentLevel.value, nextPage, pageSize)
    displayMembers.value = getCurrentTeamList()
    isLoading.value = false
}

const handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const windowHeight = window.innerHeight
    const documentHeight = document.documentElement.scrollHeight
    
    if (scrollTop + windowHeight >= documentHeight - 100) {
        loadMore()
    }
}

const switchLevel = async (level) => {
    currentLevel.value = level
    displayMembers.value = []
    await fetchTeamList(level, 1, pageSize)
    displayMembers.value = getCurrentTeamList()
}

const getLevelTitle = () => {
    switch (currentLevel.value) {
        case 1:
            return t('team.level1Members')
        case 2:
            return t('team.level2Members')
        case 3:
            return t('team.level3Members')
        default:
            return ''
    }
}

const maskName = (name) => {
  if (!name) {
    return ''
  }
  if (name.length === 1) {
    return '*'
  }
  if (name.length === 2) {
    return '**'
  }
  if (name.length === 3) {
    return '***'
  }
  const start = name.substring(0, 1)
  const end = name.substring(name.length - 1)
  const middle = '*'.repeat(3)
  return start + middle + end
}

const maskPhone = (phone) => {
  if (!phone) {
    return ''
  }
  const phoneStr = String(phone)
  if (phoneStr.length <= 4) {
    return phoneStr
  }
  const start = phoneStr.substring(0, 3)
  const end = phoneStr.substring(phoneStr.length - 4)
  const middle = '****'
  return start + middle + end
}

onMounted(async () => {
  window.scrollTo(0, 0)
  await fetchTeamInfo()
  await fetchTeamList(1, 1, pageSize)
  displayMembers.value = getCurrentTeamList()
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
.team-container {
    padding: 15px
}

.level-tabs {
    display: flex;
    gap: 10px;
    margin-bottom: 15px
}

.level-tab {
    flex: 1;
    background: var(--card-bg);
    border-radius: 12px;
    padding: 15px 10px;
    text-align: center;
    border: 2px solid var(--border-color);
    transition: all .3s ease;
    cursor: pointer;
    box-shadow: var(--shadow-sm)
}

.level-tab:active {
    transform: scale(.98)
}

.level-tab.active {
    background: linear-gradient(135deg,#667eea,#764ba2);
    border-color: transparent;
    box-shadow: 0 4px 12px #667eea4d
}

.level-tab.active .tab-title,
.level-tab.active .tab-count {
    color: #fff
}

.tab-content {
    display: flex;
    flex-direction: column;
    gap: 6px
}

.tab-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
    transition: color .3s ease
}

.tab-count {
    font-size: 20px;
    font-weight: 700;
    color: #667eea;
    transition: color .3s ease
}

.team-stats {
    display: flex;
    gap: 10px;
    margin-bottom: 15px
}

.stat-card {
    flex: 1;
    background: var(--card-bg);
    border-radius: 12px;
    padding: 15px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-shadow: var(--shadow-card)
}

.stat-card .stat-icon {
    font-size: 28px
}

.stat-card .stat-info {
    flex: 1
}

.stat-card .stat-label {
    font-size: 12px;
    color: var(--text-secondary);
    margin-bottom: 4px
}

.stat-card .stat-value {
    font-size: 18px;
    font-weight: 700;
    color: var(--text-primary)
}

.team-list {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 15px;
    box-shadow: var(--shadow-card)
}

.list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 15px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--border-color)
}

.list-header .header-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary)
}

.list-header .header-count {
    font-size: 12px;
    color: var(--text-secondary)
}

.team-items {
    display: flex;
    flex-direction: column;
    gap: 12px
}

.team-item {
    background: var(--bg-secondary);
    border-radius: 10px;
    padding: 12px;
    border: 1px solid var(--border-color);
    transition: all .3s ease
}

.team-item:active {
    transform: scale(.98)
}

.member-info {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px
}

.member-avatar {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg,#667eea,#764ba2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0
}

.member-avatar .avatar-text {
    font-size: 16px;
    font-weight: 600;
    color: #fff
}

.member-details {
    flex: 1
}

.member-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 2px
}

.member-phone {
    font-size: 12px;
    color: var(--text-secondary)
}

.member-stats {
    display: flex;
    gap: 20px;
    padding-top: 12px;
    border-top: 1px solid var(--border-color)
}

.stat-row {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px
}

.stat-row .stat-label {
    font-size: 11px;
    color: var(--text-secondary)
}

.stat-row .stat-value {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary)
}

.empty-state {
    text-align: center;
    padding: 40px 20px
}

.empty-state .empty-icon {
    font-size: 48px;
    margin-bottom: 12px
}

.empty-state .empty-text {
    font-size: 14px;
    color: var(--text-secondary)
}

.loading-more {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    gap: 10px
}

.loading-more .loading-spinner {
    width: 24px;
    height: 24px;
    border: 3px solid var(--border-color);
    border-top-color: #667eea;
    border-radius: 50%;
    animation: spin 0.8s linear infinite
}

.loading-more .loading-text {
    font-size: 12px;
    color: var(--text-secondary)
}

.no-more {
    text-align: center;
    padding: 15px
}

.no-more .no-more-text {
    font-size: 12px;
    color: var(--text-secondary)
}

@keyframes spin {
    0% {
        transform: rotate(0deg)
    }

    to {
        transform: rotate(360deg)
    }
}

@media (max-width: 375px) {
    .team-container {
        padding: 12px
    }

    .level-tabs {
        gap: 8px;
        margin-bottom: 12px
    }

    .level-tab {
        padding: 12px 8px
    }

    .tab-title {
        font-size: 12px
    }

    .tab-count {
        font-size: 18px
    }

    .team-stats {
        gap: 8px;
        margin-bottom: 12px
    }

    .stat-card {
        padding: 12px
    }

    .stat-card .stat-icon {
        font-size: 24px
    }

    .stat-card .stat-label {
        font-size: 11px
    }

    .stat-card .stat-value {
        font-size: 16px
    }

    .team-list {
        padding: 12px
    }

    .list-header {
        margin-bottom: 12px;
        padding-bottom: 10px
    }

    .list-header .header-title {
        font-size: 14px
    }

    .list-header .header-count {
        font-size: 11px
    }

    .team-item {
        padding: 10px
    }

    .member-avatar {
        width: 36px;
        height: 36px
    }

    .member-avatar .avatar-text {
        font-size: 14px
    }

    .member-name {
        font-size: 13px
    }

    .member-phone {
        font-size: 11px
    }
}
</style>
